import React, { useContext } from "react";
import { useRef, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Col, Divider, Form, Grid, Input, Popconfirm, Row, Select, Skeleton, notification } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";

import AdicionarAjuda, { ITutorial, ITutorialPassos } from "../../../Components/AdicionarAjuda";
import { BancoService } from "../../../Services/Financeiro/BancoService";
import { PropriedadesDoPopConfirm } from "../../../Defaults";
import { PropriedadesDeNotificacao } from "../../../Utils/Notificacoes";
import { TypeBanco } from "../../../Models/Financeiro/Banco";

import { Permissoes } from "../../../Constants/Permissoes";
import { SessaoContext } from "../../../Contexts/SessaoContext";
import { Restrito } from "../../../Components/Restrito";

import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../Components/Conjunto";
import { Ordenar } from "../../../Utils/Ordenacao";

import { Link, matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { useRelativePath } from "../../../Hooks/useNextRelativePath";
import { useRedirection } from "../../../Hooks/useRedirection";
import { useWatchParameterOnRoutes } from "../../../Hooks/useGetParameterOnRoutes";

import * as Tutoriais from "../Banco/banco.tutorial"
import FornecedorSelect from "../../../Components/Populados/Pessoa/Fornecedor/FornecedorSelect";

const bancoService = new BancoService();

const ListagemDeBancos: React.FC<{
    aoEditar?: (item: TypeBanco) => any,
    aoAdicionar?: () => any

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (banco: TypeBanco) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();

    const [filtro, setFiltro] = useState<TypeBanco>({} as TypeBanco);

    const listaBanco = useQuery({
        queryKey: ['listarBancos', filtro],
        queryFn: () => bancoService.listar(filtro),
        gcTime: 0,
        enabled: true
    });

    interface IFormFiltro {
        txtCodBanco?: string,
        txtNome?: string,
        ddlAtivo?: boolean
    }

    const filtrarTabela = (values: IFormFiltro) => {
        const bancoFiltro = {
            codBanco: values.txtCodBanco,
            nome: values.txtNome,
            ativo: values.ddlAtivo 
        } as TypeBanco;
        setFiltro(bancoFiltro);
    }

    const adicionar = permissoes?.includes(Permissoes.CriarBanco);

    return (
        <>
            <Form name="listar" onFinish={filtrarTabela} layout='vertical' autoComplete="off">
                <Row gutter={16} id={Tutoriais.IDs.formularioDeFiltro} >
                    <Col xs={24} md={6} id={Tutoriais.IDs.filtroDeCodigo}>
                        <Form.Item<IFormFiltro> name="txtCodBanco" label='Código'>
                            <Input placeholder="Todos"/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={7} id={Tutoriais.IDs.filtroDeNome}>
                        <Form.Item<IFormFiltro> name="txtNome" label='Nome'>
                            <Input placeholder="Todos"/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={3} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFormFiltro> name="ddlAtivo" label='Ativo' initialValue={true}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" }
                                ]} />
                        </Form.Item>
                    </Col>
                    <Col xs={adicionar ? 12 : 24} md={adicionar ? 3 : 5} style={{ alignItems: 'end', display: 'flex' }}>
                        <Form.Item style={{ flexGrow: 1 }}>
                            <Button type="primary" id={Tutoriais.IDs.botaoListar} key="submit" htmlType='submit' style={{ width: "100%" }}
                                disabled={listaBanco.isFetching}
                            >
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>
                    
                    <Restrito aberto={adicionar}>
                        <Col xs={12} md={5} style={{ alignItems: 'end', display: 'flex' }}>
                            <Form.Item style={{ flexGrow: 1 }}>
                                <Link to={props.hrefDoBotaoAdicionar ?? "#"} onClick={(e) => { !props.hrefDoBotaoAdicionar && e.preventDefault() }}>
                                    <Button type="primary" onClick={() => { props.aoAdicionar?.() }} style={{ width: "100%" }}>
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
                <Row>
                    <Col xs={24}>
                        <ResponsiveAntdTable<TypeBanco>
                            bordered
                            locale={{ emptyText: 'Nenhum banco encontrado.' }}
                            dataSource={listaBanco.data?.data}
                            loading={listaBanco.isFetching}
                            rowKey={(k) => k.codigo!}
                            addTitleOn="lg"
                            columns={[
                                {
                                    title: "Código",
                                    breakpoints: { xs: 12, sm: 12, lg: 4 },
                                    dataIndex: ["codBanco"],
                                    sorter: (a, b) => Ordenar.String(a.codBanco, b.codBanco),
                                    key: "banco-codBanco",
                                },
                                {
                                    title: "Nome",
                                    breakpoints: { xs: 24, sm: 24, lg: 14 },
                                    dataIndex: ["nome"],
                                    sorter: (a, b) => Ordenar.String(a.nome, b.nome),
                                    key: "banco-nome",
                                },
                                {
                                    title: "Ativo",
                                    dataIndex: ["ativo"],
                                    breakpoints: { xs: 12, sm: 12, lg: 2 },
                                    key: "banco-ativo",
                                    sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                    render: (v: boolean) => {
                                        return <>{v ? "Sim" : "Não"}</>
                                    }
                                },
                                {
                                    title: "Ações",
                                    key: "banco-acoes",
                                    noResponsiveTitle: true,
                                    breakpoints: { xs: 24, sm: 24, lg: 4 },
                                    hidden: [Permissoes.AtualizarBanco, Permissoes.DesativarBanco, Permissoes.AtivarBanco].every((p) => !permissoes?.includes(p)),
                                    render: (_, item) => {
                                        return <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: 'wrap' }}
                                            itens={
                                                (() => {
                                                    if (item.ativo) {
                                                        return [
                                                            permissoes?.includes(Permissoes.CriarBanco) &&
                                                            <Link to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                            >
                                                                <Button
                                                                    className={Tutoriais.Classes.botaoEditar}
                                                                    type='primary'
                                                                    size='small'
                                                                    onClick={() => {
                                                                        props.aoEditar?.(item);
                                                                    }}
                                                                >
                                                                    Editar
                                                                </Button>
                                                            </Link>,

                                                            permissoes?.includes(Permissoes.DesativarBanco) &&
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("desativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == undefined) return;
                                                                    bancoService.desativar(item.codigo)
                                                                        .then(() => {
                                                                            listaBanco.refetch()
                                                                        })
                                                                        .catch(() => { })
                                                                }}
                                                            >
                                                                <Button type='default' danger size='small' className={Tutoriais.Classes.botaoDesativar}>
                                                                    Desativar
                                                                </Button>
                                                            </Popconfirm>
                                                        ]
                                                    } else {
                                                        return [
                                                            permissoes?.includes(Permissoes.AtivarBanco) &&
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("ativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == undefined) return;
                                                                    bancoService.ativar(item.codigo)
                                                                        .then(() => {
                                                                            listaBanco.refetch()
                                                                        })
                                                                        .catch(() => { })
                                                                }}
                                                            >
                                                                <Button type='dashed' size='small'>Reativar</Button>
                                                            </Popconfirm>
                                                        ]
                                                    }

                                                })()
                                            }
                                        />
                                    }
                                },
                            ]}

                            csv={{
                                enabled: true,
                                filename: "banco.csv"
                            }}
                        />
                    </Col>
                </Row>

            </Form>

            <AdicionarAjuda tutoriais={[
                {
                    titulo: "Como consultar bancos",
                    tutorial: Tutoriais.ConsultarBanco
                },

                permissoes?.includes(Permissoes.CriarBanco) &&
                {
                    titulo: "Como cadastrar um novo banco",
                    tutorial: Tutoriais.AdicionarBanco
                },

                permissoes?.includes(Permissoes.AtualizarBanco) &&
                {
                    titulo: "Como editar um banco",
                    tutorial: Tutoriais.EditarBanco
                },

                permissoes?.includes(Permissoes.DesativarBanco) &&
                {
                    titulo: "Como desativar um banco",
                    tutorial: Tutoriais.DesativarBanco
                }
            ].filter((v) => v != false) as ITutorial[]} />
        </>
    )
}

const CadastroDeBanco: React.FC<{
    bancoEmAlteracao?: TypeBanco,
    aoCancelar?: () => any,
    depoisDeFinalizar?: () => any
}> = (props) => {
    
    const usuario = useContext(SessaoContext)?.sessao?.usuario;

    const insereBanco = useMutation({
        mutationFn: (banco: TypeBanco) => bancoService.inserir(banco),
        onSuccess: () => {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro realizado com sucesso.')
            })
            props.depoisDeFinalizar && props.depoisDeFinalizar()
        },
    });

    const atualizaBanco = useMutation({
        mutationFn: (banco: TypeBanco) => bancoService.atualizar(banco),
        onSuccess: () => { 
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Atualização realizada com sucesso.')
            })
            props.depoisDeFinalizar && props.depoisDeFinalizar()
        },
    });

    const listaBanco = useQuery({
        queryKey: ['listarBancos'],
        queryFn: () => bancoService.listar({ativo : true} as TypeBanco),
        gcTime: 0,
        enabled: true
    });

    function onFinishBanco(values: TypeBanco) {
        const bancoInserir: TypeBanco = {
            ...values,
            codigo: props.bancoEmAlteracao ? props.bancoEmAlteracao.codigo : undefined
        }

        if (bancoInserir?.codigo === undefined)
            insereBanco.mutate(bancoInserir!);
        else
            atualizaBanco.mutate(bancoInserir!);
    }

    const [formCadastro] = useForm();
    const dadosForm = useWatch([], formCadastro);

    const txtCodBanco = useRef(null)
    const txtNome = useRef(null)
    const ddlAtivo = useRef(null);
    const btnSalvar = useRef(null);

    const tutorialAdicionarBanco: ITutorialPassos = {
        steps: [
            {
                title: "Passo 1",
                description: "Informe um código para o banco.",
                target: () => txtCodBanco.current,
            },
            {
                title: "Passo 2",
                description: "Dê um nome ao banco.",
                target: () => txtNome.current,
            },
            {
                title: "Passo 3",
                description: `Clique em "Salvar".`,
                target: () => btnSalvar.current,
            }
        ]
    }
    const tutorialMenuItems: ITutorial[] = [
        {
            titulo: "Como adicionar um novo banco",
            tutorial: tutorialAdicionarBanco
        },
    ]

    return (
        <>
            <Form
                autoComplete="off"
                onFinish={onFinishBanco}
                form={formCadastro}
                layout="vertical"
            >
                <Row gutter={16}>
                    <Col xs={24} sm={6} md={6} lg={4} xl={4} xxl={3} ref={txtCodBanco}>
                        <Form.Item<TypeBanco> name="codBanco" initialValue={props.bancoEmAlteracao?.codBanco} label='Código'
                            rules={[{
                                required: true, 
                                validator: (_, value: string) => {
                                    if(value == null){
                                        return Promise.reject(new Error("Por favor, insira um código para o banco."));
                                    }

                                    if (/\s/.test(value)) {
                                        return Promise.reject(new Error("Código do banco não pode possuir espaços."));
                                    }

                                    if (listaBanco.data?.data
                                        .filter(m => props.bancoEmAlteracao ? m.codigo != props.bancoEmAlteracao.codigo : true)
                                        .map(i => i.codBanco)
                                        .includes(value)) {
                                        return Promise.reject(new Error("Código do banco já existe."));
                                    }

                                    return Promise.resolve();
                                }
                             }]
                            }>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={18} md={18} lg={8} xl={8} xxl={9} ref={txtNome}>
                        <Form.Item<TypeBanco> name="nome" rules={[{ required: true, message: 'Por favor, informe um nome!' }]} initialValue={props.bancoEmAlteracao?.nome} label='Nome'>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={18} md={18} lg={8} xl={8} xxl={9}>
                        <Form.Item<TypeBanco> label={"Fornecedor"} name={"codEmpresa"} rules={[{ required : true }]} initialValue={props.bancoEmAlteracao?.codEmpresa}>
                            <FornecedorSelect placeholder="Selecione" />
                        </Form.Item>
                    </Col>
                    {
                        props.bancoEmAlteracao &&
                        <Col xs={24} sm={6} md={6} lg={4} xl={4} xxl={3} ref={ddlAtivo}>
                            <Form.Item name="ativo" label='Ativo' rules={[{ required : true }]} initialValue={props.bancoEmAlteracao.ativo ? true : false}>
                                <Select
                                    allowClear={false}
                                    placeholder={"Todos"}
                                    options={[
                                        { value: true, label: "Sim" },
                                        { value: false, label: "Não" }
                                    ]} />
                            </Form.Item>
                        </Col>
                    }
                    <Col xs={24} style={{ alignItems: 'center', display: 'flex' }}>
                        <Form.Item>
                            {props.aoCancelar &&
                                <Button danger type="primary" onClick={() => {
                                    props.aoCancelar && props.aoCancelar()
                                }}>
                                    Cancelar
                                </Button>
                            }
                            <Button type="primary" ref={btnSalvar} key="submit" htmlType='submit' style={{ margin: '0px 8px' }}
                                disabled={!usuario?.permissoes.map((p) => p.nome).includes(props.bancoEmAlteracao != undefined ? Permissoes.AtualizarBanco : Permissoes.CriarBanco)}
                            >
                                Salvar
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <AdicionarAjuda tutoriais={tutorialMenuItems}/>
        </>
    )
}

export function Banco() {
    const [bancoEmAlteracao, setBancoEmAlteracao] = useState<TypeBanco | undefined>(undefined);
    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const navigate = useNavigate();
    const [raiz, subrota] = useRelativePath();

    const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
    const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

    const obterBanco = useMutation({
        mutationFn: (id: number) => new BancoService().listar({ codigo: id, ativo: true } as TypeBanco),
        onSuccess: (resposta) => {
            const resultados = resposta.data;
            if (resultados.length == 0) {
                notification.open({
                    ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado")
                })
                navigate(raiz)
            } else {
                setBancoEmAlteracao(resposta.data.at(0))
            }
        }
    })

    useRedirection([Permissoes.ListarBancos, Permissoes.CriarBanco, Permissoes.AtualizarBanco, Permissoes.DesativarBanco])
    useWatchParameterOnRoutes({ 
        appliablePaths: [rotaDeEdicao, rotaDeVisualizacao], paramToWatch: "id", filters: [(p) => isNaN(Number(p))], 
        onNotAccepted() { navigate(raiz) },
        onExists(param) { obterBanco.mutate(Number(param)); }, 
     })

    return ( 
        <>
            <h2 style={{ marginTop: 0 }}> 
                {
                    rotaDeVisualizacao ? "Detalhes do Banco" : rotaDeEdicao? "Edição do Banco": "Cadastro de Banco"
                }
            </h2>

            <Divider />

            <Routes>
                {
                    permissoes?.includes(Permissoes.ListarBancos) &&
                    <Route path="*" index element={
                        <ListagemDeBancos
                            hrefDoBotaoAdicionar={raiz + "/cadastro"}
                            hrefDoBotaoEditar={(item) => {
                                if (item.codigo) {
                                    return raiz + `/editar?id=${item.codigo}`
                                }
                            }}

                            aoEditar={(banco) => {
                                setBancoEmAlteracao(banco);
                            }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.CriarBanco) &&
                    <Route path="/cadastro" element={
                        <CadastroDeBanco
                            aoCancelar={() => { navigate(raiz) }}
                            depoisDeFinalizar={() => { navigate(raiz) }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.AtualizarBanco) &&
                    <Route path="/editar" element={
                        bancoEmAlteracao == undefined ? <Skeleton active /> :
                            <CadastroDeBanco
                                bancoEmAlteracao={bancoEmAlteracao}
                                aoCancelar={() => { navigate(raiz) }}
                                depoisDeFinalizar={() => { navigate(raiz) }}
                            />
                    } />
                }
            </Routes>
        </>
    );
}